import { AppState } from "@store/state";
import { Category, Product } from "@tendercuts/models/catalog";
import { flatMap, map } from "rxjs/operators";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { Store } from "@ngrx/store";

@Injectable({ providedIn: "root" })
export class SearchService {
  constructor(public store: Store<AppState>) {}

  /**
   * Extracts the list of all products from the catalog in the store.
   * @returns {Observable}
   */
  private extractProducts(): Observable<Product[]> {
    return this.store
      .select((state) => state.catalogState.catalog)
      .pipe(
        map((cat: Category[]) => {
          return cat.map((cat) => cat.products);
        }),
        // flatten out array of arrays
        map((products: Product[][]) => [].concat(...products))
      );
  }

  /**
   * Searches the catalog store based on the product name or Product object.
   * If searchTerm is 13 digits need to search for the product barcode.
   * @param {String | Product} searchTerm
   * @returns {Observable<Product[]>}
   */
  public searchProducts(searchTerm: String): Observable<Product[]> {
    let products = this.extractProducts();
    let id =
      searchTerm.length == 8
        ? searchTerm.substr(0, 3)
        : searchTerm.substr(13, 16);

    let filterFn = (product) =>
      product.id === Number(id) ||
      product.id === Number(searchTerm) ||
      product.pluCode === Number(searchTerm) ||
      product.name.toLowerCase().indexOf(searchTerm.toLowerCase()) > -1;


    let barcode13digitFilter = (product) => {
      if (!product.barcode) {
        return false;
      }
      const barcodeString: string[] = product.barcode.split(":");
      const productBarcodes: number[] = barcodeString.map((data: string) =>
        Number(data)
      );

      return productBarcodes.indexOf(Number(searchTerm)) != -1;
    };

    return products.pipe(
      flatMap((products: Product[]) => {
        return this.store
          .select((state) => state.catalogState.productMap)
          .pipe(
            map((pMap) => {
              const ids = new Set(
                products
                  .filter(
                      this.isValidBarcode(searchTerm) ? barcode13digitFilter
                      : filterFn
                  )
                  .map((product) => product.id)
              );
              const searchRes = [];
              ids.forEach((id) => searchRes.push(pMap[id]));
              return searchRes;
            })
          );
      })
    );
  }

  /**
   * returns true if its a valid number
   * @param {string} searchTerm
   */
  isNumeric(searchTerm: string) {
    return /^-?\d+$/.test(searchTerm);
  }

  /**
   * returns true if its a valid barcode
   * @param {String} searchTerm
   */
  isValidBarcode(searchTerm: String) {
    return (searchTerm.length == 13 || searchTerm.length == 12) && this.isNumeric(searchTerm.toString());
  }
}
